import { fetchYears, fetchMakes, fetchModels } from './vehicle-api.js';
import {
  applyGalleryFilters,
  readEmbeddedGalleryItems,
  sortGalleryItems,
} from './vehicle-gallery-api.js';
import { bindVehicleGalleryLightbox } from './vehicle-gallery-lightbox.js';

class VehicleGalleryPage extends HTMLElement {
  /** @type {import('@theme/vehicle-gallery-api').GalleryItem[]} */
  #allItems = [];

  /** @type {import('@theme/vehicle-gallery-api').GalleryItem[]} */
  #cachedItems = [];

  #currentPage = 1;

  #perPage = 12;

  #sort = 'position';

  #totalCount = 0;

  #loading = false;

  #metaobjectType = 'vehicle_gallery_item';

  connectedCallback() {
    this.#perPage = Number(this.dataset.defaultPerPage) || 12;
    this.#metaobjectType = this.dataset.metaobjectType || 'vehicle_gallery_item';
    this.#allItems = readEmbeddedGalleryItems();

    this.#bindControls();
    bindVehicleGalleryLightbox(this);

    const liquidCount = Number(this.dataset.liquidEntryCount);
    if (this.#allItems.length === 0 && Number.isFinite(liquidCount) && liquidCount === 0) {
      this.#renderItems([]);
      this.#updateCount();
      this.#initFilters().catch((error) => {
        console.warn('Vehicle gallery filters failed to load.', error);
      });
      return;
    }

    this.#loadView();
    this.#initFilters().catch((error) => {
      console.warn('Vehicle gallery filters failed to load.', error);
    });
  }

  #bindControls() {
    const perPageSelect = this.querySelector('[data-gallery-per-page]');
    if (perPageSelect instanceof HTMLSelectElement) {
      perPageSelect.value = String(this.#perPage);
      perPageSelect.addEventListener('change', () => {
        this.#perPage = Number(perPageSelect.value) || 12;
        this.#currentPage = 1;
        this.#loadView().catch((error) => this.#showError(error.message));
      });
    }

    const sortSelect = this.querySelector('[data-gallery-sort]');
    if (sortSelect instanceof HTMLSelectElement) {
      sortSelect.addEventListener('change', () => {
        this.#sort = sortSelect.value || 'position';
        this.#currentPage = 1;
        this.#loadView().catch((error) => this.#showError(error.message));
      });
    }

    this.querySelector('[data-gallery-filter-open]')?.addEventListener('click', () => {
      const drawer = this.querySelector('[data-gallery-filter-drawer]');
      if (drawer instanceof HTMLDialogElement) drawer.showModal();
    });

    this.querySelectorAll('[data-gallery-filter-close]').forEach((button) => {
      button.addEventListener('click', () => {
        const drawer = this.querySelector('[data-gallery-filter-drawer]');
        if (drawer instanceof HTMLDialogElement) drawer.close();
      });
    });

    this.querySelector('[data-gallery-filter-clear]')?.addEventListener('click', () => {
      this.#setFilterValue('year', '');
      this.#setFilterValue('make', '');
      this.#setFilterValue('model', '');
      this.#setFilterValue('wheel', '');
      this.#setFilterDisabled('make', true);
      this.#setFilterDisabled('model', true);
      this.#currentPage = 1;
      this.#loadView().catch((error) => this.#showError(error.message));
    });

    this.querySelectorAll('[data-gallery-filter]').forEach((field) => {
      field.addEventListener('change', () => this.#onFilterChange(field));
    });

    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const carouselControl = target.closest('[data-carousel-prev], [data-carousel-next], [data-carousel-dot]');
      if (carouselControl instanceof HTMLElement) {
        event.preventDefault();
        event.stopPropagation();
        const carousel = carouselControl.closest('[data-gallery-carousel]');
        if (carousel instanceof HTMLElement) {
          this.#handleCarouselClick(carousel, carouselControl);
        }
        return;
      }

      const pageButton = target.closest('[data-gallery-page]');
      if (!(pageButton instanceof HTMLElement)) return;
      event.preventDefault();
      this.#currentPage = Number(pageButton.dataset.galleryPage) || 1;
      this.#loadView().catch((error) => this.#showError(error.message));
      this.querySelector('[data-vehicle-gallery-grid]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  async #initFilters() {
    try {
      const years = await fetchYears();
      this.#setFilterOptions(
        'year',
        ['<option value="">All years</option>'].concat(
          years.map((year) => `<option value="${this.#escape(year)}">${this.#escape(year)}</option>`)
        )
      );
    } catch (error) {
      console.warn('Vehicle gallery year filter unavailable.', error);
      this.#setFilterOptions('year', ['<option value="">All years</option>']);
    }

    this.#setFilterOptions('make', ['<option value="">All makes</option>']);
    this.#setFilterOptions('model', ['<option value="">All models</option>']);
    this.#setFilterDisabled('make', true);
    this.#setFilterDisabled('model', true);

    this.#refreshWheelOptions();
    this.#syncPerPageSelect();
  }

  /** @param {Element} field */
  async #onFilterChange(field) {
    if (!(field instanceof HTMLSelectElement)) return;

    const filterName = field.dataset.galleryFilter || '';
    this.#syncFilterGroup(filterName, field.value);

    if (filterName === 'year') {
      const year = field.value;
      this.#setFilterValue('make', '');
      this.#setFilterValue('model', '');
      this.#setFilterOptions('make', ['<option value="">All makes</option>']);
      this.#setFilterOptions('model', ['<option value="">All models</option>']);
      this.#setFilterDisabled('model', true);

      if (year) {
        this.#setFilterDisabled('make', true);
        const makes = await fetchMakes(year);
        this.#setFilterOptions(
          'make',
          ['<option value="">All makes</option>'].concat(
            makes.map(
              (make) =>
                `<option value="${this.#escape(make.value)}">${this.#escape(make.label)}</option>`
            )
          )
        );
        this.#setFilterDisabled('make', false);
      } else {
        this.#setFilterDisabled('make', true);
      }
    }

    if (filterName === 'make') {
      const year = this.#getFilterValue('year');
      const make = field.value;
      this.#setFilterValue('model', '');
      this.#setFilterOptions('model', ['<option value="">All models</option>']);

      if (year && make) {
        this.#setFilterDisabled('model', true);
        const models = await fetchModels(year, make);
        this.#setFilterOptions(
          'model',
          ['<option value="">All models</option>'].concat(
            models.map(
              (model) =>
                `<option value="${this.#escape(model.value)}">${this.#escape(model.label)}</option>`
            )
          )
        );
        this.#setFilterDisabled('model', false);
      } else {
        this.#setFilterDisabled('model', true);
      }
    }

    this.#currentPage = 1;
    await this.#loadView();
  }

  /** @returns {import('@theme/vehicle-gallery-api').GalleryFilters} */
  #getFilters() {
    return {
      year: this.#getFilterValue('year'),
      make: this.#getFilterValue('make'),
      model: this.#getFilterValue('model'),
      wheel: this.#getFilterValue('wheel'),
    };
  }

  #refreshWheelOptions() {
    /** @type {Map<string, string>} */
    const wheels = new Map();

    this.#allItems.forEach((item) => {
      if (item.wheelHandle) {
        wheels.set(item.wheelHandle, item.wheelLabel || item.wheelHandle);
      }
    });

    this.#setFilterOptions(
      'wheel',
      ['<option value="">All products</option>'].concat(
        [...wheels.entries()]
          .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: 'base' }))
          .map(
            ([handle, label]) =>
              `<option value="${this.#escape(handle)}">${this.#escape(label)}</option>`
          )
      )
    );
  }

  async #loadView() {
    if (this.#loading) return;
    this.#loading = true;
    this.#showLoading();

    try {
      const filters = this.#getFilters();
      this.#cachedItems = sortGalleryItems(applyGalleryFilters(this.#allItems, filters), this.#sort);
      this.#totalCount = this.#cachedItems.length;

      const totalPages = Math.max(1, Math.ceil(this.#totalCount / this.#perPage));
      if (this.#currentPage > totalPages) this.#currentPage = totalPages;

      const start = (this.#currentPage - 1) * this.#perPage;
      this.#renderItems(this.#cachedItems.slice(start, start + this.#perPage));
      this.#updateCount();
      this.#renderPagination(this.#currentPage < totalPages);
    } finally {
      this.#loading = false;
    }
  }

  /** @param {import('@theme/vehicle-gallery-api').GalleryItem[]} items */
  #renderItems(items) {
    const grid = this.querySelector('[data-vehicle-gallery-grid]');
    const empty = this.querySelector('[data-gallery-empty]');
    const error = this.querySelector('[data-gallery-error]');
    const loading = this.querySelector('[data-gallery-loading]');

    if (!(grid instanceof HTMLElement)) return;

    if (error instanceof HTMLElement) error.hidden = true;
    if (loading instanceof HTMLElement) loading.hidden = true;

    if (items.length === 0) {
      grid.innerHTML = '';
      if (empty instanceof HTMLElement) {
        empty.hidden = false;
        this.#updateEmptyHint();
      }
      return;
    }

    if (empty instanceof HTMLElement) empty.hidden = true;

    const placeholderAssets = (this.dataset.placeholderAssets || '')
      .split('|')
      .map((asset) => asset.trim())
      .filter(Boolean);

    grid.innerHTML = items
      .map((item, index) => this.#renderCard(item, index, placeholderAssets))
      .join('');
  }

  #normalizeImageUrl(url) {
    const value = String(url || '').trim();
    if (value.startsWith('//')) return `https:${value}`;
    return value;
  }

  /**
   * @param {import('./vehicle-gallery-api.js').GalleryItem} item
   * @returns {import('./vehicle-gallery-api.js').GalleryImage[]}
   */
  #getItemImages(item) {
    if (item.images?.length) {
      return item.images.map((image) => ({
        ...image,
        url: this.#normalizeImageUrl(image.url),
      }));
    }
    if (item.imageUrl) {
      return [
        {
          url: this.#normalizeImageUrl(item.imageUrl),
          alt: item.imageAlt,
          width: item.imageWidth,
          height: item.imageHeight,
        },
      ];
    }
    return [];
  }

  /**
   * @param {import('@theme/vehicle-gallery-api').GalleryImage[]} images
   */
  #encodeImagesAttr(images) {
    return JSON.stringify(images).replace(/&/g, '&amp;').replace(/'/g, '&#39;').replace(/</g, '&lt;');
  }

  /**
   * @param {import('@theme/vehicle-gallery-api').GalleryItem} item
   * @param {string} placeholder
   */
  #renderMedia(item, placeholder) {
    const images = this.#getItemImages(item);

    if (images.length === 0) {
      return placeholder
        ? `<div class="vehicle-gallery__media"><img class="vehicle-gallery__image vehicle-gallery__image--placeholder" src="${this.#escape(placeholder)}" alt="${this.#escape(item.cardTitle)}" loading="lazy" width="900" height="675"></div>`
        : '<div class="vehicle-gallery__media"></div>';
    }

    if (images.length === 1) {
      const image = images[0];
      return `
        <div
          class="vehicle-gallery__media"
          data-gallery-lightbox
          data-carousel-index="0"
          data-carousel-images='${this.#encodeImagesAttr(images)}'
        >
          <img
            class="vehicle-gallery__image"
            src="${this.#escape(image.url)}"
            alt="${this.#escape(image.alt)}"
            loading="lazy"
            width="${image.width || 900}"
            height="${image.height || 675}"
          >
        </div>
      `;
    }

    const firstImage = images[0];
    const dots = images
      .map(
        (_, dotIndex) =>
          `<button type="button" class="vehicle-gallery__carousel-dot${dotIndex === 0 ? ' is-active' : ''}" data-carousel-dot="${dotIndex}" aria-label="Image ${dotIndex + 1} of ${images.length}"></button>`
      )
      .join('');

    return `
      <div
        class="vehicle-gallery__media vehicle-gallery__media--carousel"
        data-gallery-lightbox
        data-gallery-carousel
        data-carousel-index="0"
        data-carousel-images='${this.#encodeImagesAttr(images)}'
      >
        <img
          class="vehicle-gallery__image"
          data-carousel-active
          src="${this.#escape(firstImage.url)}"
          alt="${this.#escape(firstImage.alt)}"
          loading="lazy"
          width="${firstImage.width || 900}"
          height="${firstImage.height || 675}"
        >
        <button type="button" class="vehicle-gallery__carousel-btn vehicle-gallery__carousel-btn--prev" data-carousel-prev aria-label="Previous image">‹</button>
        <button type="button" class="vehicle-gallery__carousel-btn vehicle-gallery__carousel-btn--next" data-carousel-next aria-label="Next image">›</button>
        <div class="vehicle-gallery__carousel-dots" data-carousel-dots>${dots}</div>
      </div>
    `;
  }

  /** @param {HTMLElement} carousel @param {HTMLElement} control */
  #handleCarouselClick(carousel, control) {
    const images = this.#readCarouselImages(carousel);
    if (!images.length) return;

    const currentIndex = Number(carousel.dataset.carouselIndex) || 0;

    if (control.hasAttribute('data-carousel-prev')) {
      this.#setCarouselIndex(carousel, currentIndex - 1, images);
      return;
    }

    if (control.hasAttribute('data-carousel-next')) {
      this.#setCarouselIndex(carousel, currentIndex + 1, images);
      return;
    }

    if (control.hasAttribute('data-carousel-dot')) {
      this.#setCarouselIndex(carousel, Number(control.getAttribute('data-carousel-dot')) || 0, images);
    }
  }

  /** @param {HTMLElement} carousel */
  #readCarouselImages(carousel) {
    const raw = carousel.getAttribute('data-carousel-images');
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw.replace(/&amp;/g, '&').replace(/&#39;/g, "'").replace(/&lt;/g, '<'));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {HTMLElement} carousel
   * @param {number} index
   * @param {import('@theme/vehicle-gallery-api').GalleryImage[]} images
   */
  #setCarouselIndex(carousel, index, images) {
    if (!images.length) return;

    const normalized = ((index % images.length) + images.length) % images.length;
    const image = images[normalized];
    carousel.dataset.carouselIndex = String(normalized);

    const activeImage = carousel.querySelector('[data-carousel-active]');
    if (activeImage instanceof HTMLImageElement) {
      activeImage.src = image.url;
      activeImage.alt = image.alt;
      if (image.width) activeImage.width = image.width;
      if (image.height) activeImage.height = image.height;
    }

    carousel.querySelectorAll('[data-carousel-dot]').forEach((dot, dotIndex) => {
      if (dot instanceof HTMLElement) {
        dot.classList.toggle('is-active', dotIndex === normalized);
      }
    });
  }

  /**
   * @param {import('@theme/vehicle-gallery-api').GalleryItem} item
   * @param {number} index
   * @param {string[]} placeholderAssets
   */
  #renderCard(item, index, placeholderAssets) {
    const placeholder =
      placeholderAssets[index % placeholderAssets.length] || placeholderAssets[0] || '';
    const mediaHtml = this.#renderMedia(item, placeholder);
    const metaHtml = `
      <div class="vehicle-gallery__meta">
        ${
          item.cardTitle
            ? `<button type="button" class="vehicle-gallery__vehicle vehicle-gallery__lightbox-trigger" data-gallery-lightbox>${this.#escape(item.cardTitle)}</button>`
            : ''
        }
        ${
          item.wheelLabel && item.cardUrl
            ? `<a class="vehicle-gallery__wheel" href="${this.#escape(item.cardUrl)}">${this.#escape(item.wheelLabel)}</a>`
            : item.wheelLabel
              ? `<p class="vehicle-gallery__wheel">${this.#escape(item.wheelLabel)}</p>`
              : ''
        }
      </div>
    `;

    const cardInner = `<div class="vehicle-gallery__card-body">${mediaHtml}${metaHtml}</div>`;

    return `
      <li
        class="vehicle-gallery__item"
        data-vehicle-gallery-item
        data-year="${this.#escape(item.yearLabel || item.vehicleYear)}"
        data-year-start="${item.yearStart ?? ''}"
        data-year-end="${item.yearEndIsPresent ? 'present' : item.yearEnd ?? ''}"
        data-make="${this.#escape(item.makeSlug)}"
        data-model="${this.#escape(item.modelSlug)}"
        data-wheel="${this.#escape(item.wheelHandle)}"
        data-wheel-label="${this.#escape(item.wheelLabel)}"
        data-sort-name="${this.#escape(item.sortName)}"
      >
        <div class="vehicle-gallery__card">
          ${cardInner}
        </div>
      </li>
    `;
  }

  #showLoading() {
    const grid = this.querySelector('[data-vehicle-gallery-grid]');
    const empty = this.querySelector('[data-gallery-empty]');
    const error = this.querySelector('[data-gallery-error]');
    const loading = this.querySelector('[data-gallery-loading]');

    if (empty instanceof HTMLElement) empty.hidden = true;
    if (error instanceof HTMLElement) error.hidden = true;
    if (grid instanceof HTMLElement) grid.innerHTML = '';
    if (loading instanceof HTMLElement) loading.hidden = false;
  }

  #updateEmptyHint() {
    const hint = this.querySelector('[data-gallery-empty-hint]');
    const message = this.querySelector('[data-gallery-empty-message]');
    const liquidCount = Number(this.dataset.liquidEntryCount);
    const type = this.#metaobjectType;
    const hasFilters = Object.values(this.#getFilters()).some(Boolean);

    if (message) {
      message.textContent = hasFilters ? 'No gallery items match your filters.' : 'No gallery items yet.';
    }

    if (!(hint instanceof HTMLElement)) return;

    if (hasFilters) {
      hint.textContent = 'Adjust your filters, or click Clear all to reset.';
      return;
    }

    if (this.#allItems.length > 0) {
      hint.textContent = '';
      return;
    }

    if (Number.isFinite(liquidCount) && liquidCount > 0) {
      hint.textContent = `The theme loaded ${liquidCount} "${type}" entries but could not parse them. Refresh the page; if still empty, contact your developer to check the data format.`;
      return;
    }

    hint.textContent = `No gallery entries found for type "${type}". In Admin go to Content → Metaobjects and confirm: type handle is ${type}, entries are Active, and the images field has install photos.`;
  }

  /** @param {string} message */
  #showError(message) {
    const loading = this.querySelector('[data-gallery-loading]');
    const empty = this.querySelector('[data-gallery-empty]');
    const error = this.querySelector('[data-gallery-error]');
    const grid = this.querySelector('[data-vehicle-gallery-grid]');

    if (loading instanceof HTMLElement) loading.hidden = true;
    if (empty instanceof HTMLElement) empty.hidden = true;
    if (grid instanceof HTMLElement) grid.innerHTML = '';
    if (error instanceof HTMLElement) {
      error.hidden = false;
      const messageEl = error.querySelector('[data-gallery-error-message]');
      if (messageEl) messageEl.textContent = message;
    }
  }

  #updateCount() {
    const countEl = this.querySelector('[data-gallery-count]');
    if (!countEl) return;

    countEl.textContent = `(${this.#totalCount})`;
  }

  /** @param {boolean} [hasNextPage] */
  #renderPagination(hasNextPage = false) {
    const nav = this.querySelector('[data-gallery-pagination]');
    if (!(nav instanceof HTMLElement)) return;

    const useNumberedPages = this.#sort === 'name-asc' || this.#sort === 'name-desc';
    const totalPages = Math.max(1, Math.ceil(this.#totalCount / this.#perPage));

    if (!useNumberedPages) {
      const showPrev = this.#currentPage > 1;
      const showNext = hasNextPage;

      if (!showPrev && !showNext) {
        nav.innerHTML = '';
        nav.hidden = true;
        return;
      }

      nav.hidden = false;
      nav.innerHTML = `
        <ul class="vehicle-gallery__pagination-list" role="list">
          ${showPrev ? `<li><button type="button" class="vehicle-gallery__page-link" data-gallery-page="${this.#currentPage - 1}">Previous</button></li>` : ''}
          <li><span class="vehicle-gallery__page-current">Page ${this.#currentPage}</span></li>
          ${showNext ? `<li><button type="button" class="vehicle-gallery__page-link" data-gallery-page="${this.#currentPage + 1}">Next</button></li>` : ''}
        </ul>
      `;
      return;
    }

    if (totalPages <= 1) {
      nav.innerHTML = '';
      nav.hidden = true;
      return;
    }

    nav.hidden = false;
    const items = [];

    for (let page = 1; page <= totalPages; page += 1) {
      const isCurrent = page === this.#currentPage;
      items.push(
        `<li><button type="button" class="vehicle-gallery__page-link${isCurrent ? ' is-active' : ''}" data-gallery-page="${page}" ${isCurrent ? 'aria-current="page"' : ''}>${page}</button></li>`
      );
    }

    nav.innerHTML = `<ul class="vehicle-gallery__pagination-list" role="list">${items.join('')}</ul>`;
  }

  #syncPerPageSelect() {
    const perPageSelect = this.querySelector('[data-gallery-per-page]');
    if (perPageSelect instanceof HTMLSelectElement) {
      perPageSelect.value = String(this.#perPage);
    }
  }

  /** @param {string} name @param {string} value */
  #syncFilterGroup(name, value) {
    this.querySelectorAll(`[data-gallery-filter="${name}"]`).forEach((field) => {
      if (field instanceof HTMLSelectElement) field.value = value;
    });
  }

  /** @param {string} name */
  #getFilterValue(name) {
    const field = this.querySelector(`[data-gallery-filter="${name}"]`);
    return field instanceof HTMLSelectElement ? field.value : '';
  }

  /** @param {string} name @param {string} value */
  #setFilterValue(name, value) {
    this.#syncFilterGroup(name, value);
  }

  /** @param {string} name @param {boolean} disabled */
  #setFilterDisabled(name, disabled) {
    this.querySelectorAll(`[data-gallery-filter="${name}"]`).forEach((field) => {
      if (field instanceof HTMLSelectElement) field.disabled = disabled;
    });
  }

  /** @param {string} name @param {string[]} options */
  #setFilterOptions(name, options) {
    const html = options.join('');
    this.querySelectorAll(`[data-gallery-filter="${name}"]`).forEach((field) => {
      if (field instanceof HTMLSelectElement) field.innerHTML = html;
    });
  }

  /** @param {string} value */
  #escape(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

if (!customElements.get('vehicle-gallery-page')) {
  customElements.define('vehicle-gallery-page', VehicleGalleryPage);
}
